import { computed, onBeforeUnmount, ref, watch } from 'vue';

export type LinkItem = {
  title: string;
  link: string;
};

export type SearchResponse = {
  forum?: LinkItem[];
  wiki?: LinkItem[];
  helpfulLinks?: LinkItem[];
};

export default function useSearch(debounceMs = 500) {
  const query = ref<string>('');
  const loading = ref(false);
  const error = ref<string | null>(null);
  const data = ref<SearchResponse | null>(null);

  let timer: ReturnType<typeof setTimeout> | null = null;
  let controller: AbortController | null = null;

  const hasResults = computed(() => {
    return !!(data.value?.wiki?.length || data.value?.forum?.length);
  });

  async function executeSearch(searchQuery: string) {
    if (!searchQuery) {
      data.value = null;
      error.value = null;
      return;
    }

    // Cancel previous request
    if (controller) controller.abort();
    controller = new AbortController();

    loading.value = true;
    error.value = null;

    try {
      const res = await $fetch<SearchResponse>(
        `https://search.g-portal.com/all/${encodeURIComponent('en')}/${encodeURIComponent(searchQuery)}`,
        {
          signal: controller.signal,
          retry: 0,
        },
      );
      data.value = res;
    } catch (e: unknown) {
      if (e instanceof Error && e.name === 'AbortError') return;
      error.value = e instanceof Error ? e.message : 'Request failed';
      data.value = null;
    } finally {
      loading.value = false;
    }
  }

  // Debounced search on query change
  watch(query, (val) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => executeSearch(val.trim()), debounceMs);
  });

  // Cleanup
  onBeforeUnmount(() => {
    if (timer) clearTimeout(timer);
    if (controller) controller.abort();
  });

  return {
    state: reactive({
      query,
      loading: readonly(loading),
      error: readonly(error),
      data: readonly(data),
    }),
    hasResults,
    executeSearch,
  };
}
